<template>
  <div :id="modalId" class="modal overflow-y-auto flex" tabindex="-1" aria-hidden="true" :class="{ show: isOpen }">
    <div class="modal-dialog">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header px-5 py-3 border-b border-gray-200 dark:border-dark-5">
          <h2 class="font-medium text-base">
            {{ $t("views.science_source_platforms.new") + getRegionName(platform.regionOfOrigin) }}
          </h2>
        </div>

        <!-- Modal Body -->
        <div class="modal-body px-5 py-6">
          <!-- Name Input -->
          <div>
            <label for="quick-platform-name" class="form-label font-semibold">
              {{ $t("views.science_source_platforms.form.name.label") }}
            </label>
            <div class="input-group">
              <input
                id="quick-platform-name"
                type="text"
                class="form-control w-full bg-theme-32"
                :class="platform.validations.name ? 'border border-theme-6' : ''"
                :placeholder="$t('views.science_source_platforms.form.name.placeholder')"
                v-model="platform.name"
                maxlength="110"
                @input="clearValidationError('name')"
              />
            </div>
            <div v-if="platform.validations.name" class="text-theme-6 mt-2 text-sm">
              {{ platform.validations.name }}
            </div>
          </div>

          <!-- Region of origin field -->
          <!-- <div class="mt-5">
            <label for="quick-platform-region" class="form-label font-semibold">
              {{ $t('views.science_source_platforms.form.region.label') }}
            </label>
            <div class="input-group">
              <TomSelect id="quick-platform-region" class="form-select w-full bg-theme-32"
                :class="platform.validations.regionOfOrigin ? 'border border-theme-6' : ''"
                v-model="platform.regionOfOrigin" @change="clearValidationError('regionOfOrigin')">
                <option value="">{{ $t('views.science_source_platforms.form.region.placeholder') }}...</option>
                <option value="WESTERN">Western / Occidente</option>
                <option value="EASTERN">Eastern / Oriente</option>
              </TomSelect>
            </div>
            <div v-if="platform.validations.regionOfOrigin" class="text-theme-6 mt-2 text-sm">
              {{ platform.validations.regionOfOrigin }}
            </div>
          </div> -->

          <!-- Link Page Input -->
          <div class="mt-5">
            <label for="quick-platform-link" class="form-label font-semibold">
              {{ $t("views.science_source_platforms.form.link.label") }}
            </label>
            <div class="input-group">
              <input
                id="quick-platform-link"
                type="url"
                class="form-control w-full bg-theme-32"
                :class="platform.validations.linkPage ? 'border border-theme-6' : ''"
                :placeholder="$t('views.science_source_platforms.form.link.placeholder')"
                v-model="platform.linkPage"
                @input="clearValidationError('linkPage')"
              />
            </div>
            <div v-if="platform.validations.linkPage" class="text-theme-6 mt-2 text-sm">
              {{ platform.validations.linkPage }}
            </div>
          </div>

          <!-- Error messages -->
          <div v-if="apiError" class="text-theme-6 mt-3 text-sm font-semibold">
            {{ apiError }}
          </div>
        </div>

        <!-- Modal Footer -->
        <div
          class="modal-footer px-5 py-3 border-t border-gray-200 dark:border-dark-5 text-right flex justify-end gap-3"
        >
          <button type="button" @click="close" class="btn btn-outline-secondary w-24 mr-2" :disabled="loading">
            {{ $t("views.cancel") }}
          </button>
          <button
            type="button"
            @click="save"
            class="btn btn-primary w-24 flex items-center justify-center"
            :disabled="loading"
          >
            <LoadingIcon v-if="loading" icon="three-dots" class="w-4 h-4 ml-2" />
            <span v-else>{{ $t("views.save") }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import ScienceSourceService from "@/services/science-source.service";
import { ensureShape, validatePlatform, formatToSend } from "@/utils/modules/science-source/platforms.js";
import Toastify from "toastify-js";

export default defineComponent({
  name: "PlatformQuickCreateModal",
  props: {
    modalId: {
      type: String,
      default: "platform-quick-create-modal",
    },
  },
  emits: ["created"],
  data() {
    return {
      isOpen: false,
      loading: false,
      platform: {
        id: null,
        name: "",
        regionOfOrigin: "",
        linkPage: "",
        validations: {
          name: "",
          regionOfOrigin: "",
          linkPage: "",
        },
      },
      apiError: "",
      lang_key: localStorage.lang ? localStorage.lang : navigator.language.split("-")[0],
    };
  },
  created() {
    this.resetForm();
  },
  methods: {
    resetForm() {
      this.platform = ensureShape({
        id: null,
        name: "",
        regionOfOrigin: "",
        linkPage: "",
      });
      this.apiError = "";
    },
    show(defaultRegion = "") {
      this.resetForm();
      if (defaultRegion) {
        this.platform.regionOfOrigin = defaultRegion;
      }
      this.isOpen = true;
      cash("#" + this.modalId).modal("show");
    },
    close() {
      this.isOpen = false;
      cash("#" + this.modalId).modal("hide");
    },
    clearValidationError(field) {
      if (this.platform.validations) {
        this.platform.validations[field] = "";
      }
    },
    async save() {
      this.apiError = "";

      const isValid = validatePlatform(this.platform, {
        t: this.$t,
      });

      if (!isValid) {
        return;
      }

      this.loading = true;

      try {
        const [payload] = formatToSend(this.platform);
        const response = await ScienceSourceService.create(payload, this.lang_key);

        let newPlatform = null;
        if (response && response.data) {
          newPlatform = response.data;
        }

        console.log("newPlatform", newPlatform);

        // Emit event to parent with the newly created platform details
        this.$emit("created", {
          id: newPlatform?.id || response?.id,
          name: newPlatform?.name || this.platform.name,
          region_of_origin:
            newPlatform?.region_of_origin || newPlatform?.regionOfOrigin || this.platform.regionOfOrigin,
        });

        this.showToast();
        this.close();
      } catch (error) {
        console.error("Error creating science platform via quick modal:", error);
        this.apiError =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Error al guardar la plataforma.";
      } finally {
        this.loading = false;
      }
    },
    showToast() {
      const customNode = cash(`
        <div class="toastify-content flex">
          <div class="text-theme-9">✓</div>
          <div class="ml-4 mr-4">
            <div class="font-medium">Guardado con éxito.</div>
          </div>
        </div>
      `)[0];
      Toastify({
        node: customNode,
        duration: 3000,
        gravity: "top",
        position: "right",
        close: true,
      }).showToast();
    },

    // Data
    getRegionName(region) {
      const regionName = region?.toUpperCase() ?? "";

      if (regionName == "") return "";

      const translationKey = `views.science_source_platforms.regions.${regionName}`;
      const translatedText = this.$t(translationKey);

      if (translatedText !== translationKey) {
        return ` ${translatedText.toLowerCase()}`;
      }

      // Fallback
      return ` ${regionName.toLowerCase()}`;
    },
  },
});
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
